import {
  ClientPaymentStatus,
  Div,
  FinancesSaasPaymentEntity,
  FinancesSaasStatusEntity,
  formatAmount,
  lightTheme,
  ListSpinner,
  NumberType,
  P,
  Tab,
  TableHeader,
  TabsWrapper,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { SAAS_STATUS_LABEL, SaasRange } from './saasDetails.helpers';
import { PAYMENT_COLUMNS, SaasPaymentRow } from './SaasPaymentRow';
import { SaasRetryModal } from './SaasRetryModal';
import { useRetrySaasPayment } from './useRetrySaasPayment';
import { useSaasPayments } from './useSaasPayments';

type Props = {
  range: SaasRange;
  /** All five, from the summary: the tabs' counts and amounts. */
  statuses: FinancesSaasStatusEntity[];
};

/**
 * The monthly payments of the range: a tab per status (count and amount;
 * only `completed` is in the totals above, the rest are for reading) that
 * filters the paged list under it.
 */
export const SaasPaymentsSection = ({ range, statuses }: Props) => {
  const [status, setStatus] = useState<ClientPaymentStatus | null>(null);
  const {
    payments,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useSaasPayments(range, status, true);
  const [sentinelRef, sentinelInView] = useInView();
  const [retrying, setRetrying] = useState<FinancesSaasPaymentEntity | null>(
    null
  );
  const { busy, retry } = useRetrySaasPayment();

  useEffect(() => {
    if (sentinelInView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [sentinelInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const totalCount = statuses.reduce((sum, item) => sum + item.count, 0);

  return (
    <>
      {/* The wrapper's padding is what leaves the selected tab room for its
          shadow; sized to the modal's own 24px here instead of the page's
          50px, which would push the row out past the modal's edges. */}
      <TabsWrapper style={{ padding: '0 24px 24px', margin: '0 -24px' }}>
        <Tab selected={status === null} onClick={() => setStatus(null)}>
          All ({totalCount})
        </Tab>
        {statuses.map((item) => (
          <Tab
            key={item.status}
            selected={status === item.status}
            onClick={() => setStatus(item.status)}
          >
            {SAAS_STATUS_LABEL[item.status]} ({item.count}) ·{' '}
            {formatAmount(item.amount, NumberType.USD)}
          </Tab>
        ))}
      </TabsWrapper>

      <TableHeader columns={PAYMENT_COLUMNS} padding="0 16px" gap="12px" />

      {payments.map((payment) => (
        <SaasPaymentRow
          key={payment.id}
          payment={payment}
          onRetry={setRetrying}
        />
      ))}

      {!isLoading && !isError && payments.length === 0 && (
        <Div $flex $center $height="100px">
          <P $m="0" $color={lightTheme.lightGray}>
            {status
              ? `No ${SAAS_STATUS_LABEL[
                  status
                ].toLowerCase()} payments in this period.`
              : 'No monthly payments in this period.'}
          </P>
        </Div>
      )}

      {isError && (
        <Div $flex $center $height="100px">
          <P $m="0" $color={lightTheme.primary}>
            The payments could not be loaded.
          </P>
        </Div>
      )}

      <ListSpinner
        loading={isLoading || isFetchingNextPage}
        observerRef={sentinelRef}
        mb="0"
        mbSM="0"
      />

      <SaasRetryModal
        payment={retrying}
        busy={busy}
        onClose={() => setRetrying(null)}
        onConfirm={() =>
          retrying ? retry(retrying.id) : Promise.resolve(false)
        }
      />
    </>
  );
};
